import * as S from "./styles.ts";
import { useEffect, useState } from "react";
import StandardOptions from "@template/blocks/NumberPickerSection/component/NumberPickerTabs/NumberPickerTab/StandardOptions";
import Footer from "@template/blocks/NumberPickerSection/component/NumberPickerTabs/NumberPickerTab/Footer";
import { AnimatePresence } from "framer-motion";
import AdvancedOptions from "@template/blocks/NumberPickerSection/component/NumberPickerTabs/NumberPickerTab/AdvancedOptions";
import { useTypedDispatch, useTypedSelector } from "@/redux/helpers/hooks.ts";
import { getNumbers } from "@/redux/slices/numbers/thunks/getNumbers.ts";
import { changeNumberOption, clearNumbers } from "@/redux/slices/numbers";
import { useRouter } from "@/lib/helpers/hooks/useRouter.ts";
import { useSearchParams } from "next/navigation";

interface INumberPickerTabProps {
  defaultCountryCode: string;
  defaultAreaCode: string;
  defaultType: string;
  context?: "virtualLandline" | "individual" | "standard" | "transfer";
  setShowTable?: (show: boolean) => void;
  shownTable?: { standard?: boolean; memorable?: boolean; nonGeo?: boolean };
}

const NumberPickerTab = ({
  defaultCountryCode,
  defaultAreaCode,
  defaultType,
  context,
  setShowTable,
  shownTable,
}: INumberPickerTabProps) => {
  const noAutoSearch =
    context === "virtualLandline" || context === "individual";
  const router = useRouter();
  const dispatch = useTypedDispatch();
  const searchParams = useSearchParams();

  const [mounted, setMounted] = useState(false);

  const standardLoading = useTypedSelector(
    (state) => state.numbers.numbers.standard.loading
  );
  const premiumLoading = useTypedSelector(
    (state) => state.numbers.numbers.premium.loading
  );
  const numbersData = useTypedSelector((state) => state.numbers.numbers);
  const loading = standardLoading || premiumLoading;

  const countries = useTypedSelector((state) => state.numbers.countries);
  const options = useTypedSelector((state) => state.numbers.options);

  const showAdvanced = options.showAdvanced;

  const countryCode = options.countryCode;

  const areaCode = options.areaCode;

  const numberCategory = options.numberCategory;
  const limit = options.limit;

  const startsWith = options.startsWith;
  const contains = options.contains;
  const endsWith = options.endsWith;

  const foundCountry = countries.find((c) => c.value === countryCode);
  const foundAreaCode = foundCountry?.area_codes.find(
    (a) => a.area_code === areaCode
  );
  const actualAreaCode = foundAreaCode?.area_code || "";

  const scrollToView = () => {
    const hasStandard = numbersData.standard?.numbers?.length > 0;
    const hasPremium = numbersData.premium?.numbers?.length > 0;

    let categoryToScroll = numberCategory;

    if (numberCategory === "all" && loading) {
      categoryToScroll = hasStandard
        ? "standard"
        : hasPremium
          ? "premium"
          : "standard";
    }

    const targetId = `number-table-${categoryToScroll === "all" ? "standard" : categoryToScroll}`;

    const element = document.getElementById(targetId);
    if (element) {
      const timer = setTimeout(() => {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);

      return () => clearTimeout(timer);
    }
  };

  const handleSearch = () => {
    dispatch(clearNumbers());
    if (!loading && mounted) return;
    if ((actualAreaCode || areaCode) && !mounted) {
      const searchWithPattern =
        (showAdvanced && (contains || endsWith || startsWith)) ||
        foundAreaCode?.area_code_block ||
        false;
      const foundCountry = countries.find((c) =>
        [countryCode].includes(c.value)
      );
      dispatch(
        getNumbers({
          countryCode: foundCountry?.countryCode,
          type: numberCategory || defaultType,
          areaCode: foundAreaCode
            ? foundAreaCode.search_key
            : areaCode || defaultAreaCode,
          pattern: searchWithPattern
            ? `${foundAreaCode?.area_code_block || ""}${startsWith}*${contains}*${endsWith}`
            : "",
          limit: limit,
        })
      );
      if (searchParams?.get("scroll") === "true") {
        scrollToView();
      }
    }
  };

  useEffect(() => {
    dispatch(
      changeNumberOption({ key: "countryCode", value: defaultCountryCode })
    );
    dispatch(changeNumberOption({ key: "areaCode", value: defaultAreaCode }));
    dispatch(changeNumberOption({ key: "numberType", value: defaultType }));
  }, [defaultCountryCode, defaultAreaCode, defaultType]);

  useEffect(() => {
    if (!mounted && !noAutoSearch) {
      setMounted(true);
      handleSearch();
    }
  }, [mounted]);

  useEffect(() => {
    if (mounted) {
      setMounted(false);
    }
  }, [
    defaultCountryCode,
    defaultAreaCode,
    defaultType,
    areaCode,
    countryCode,
    numberCategory,
    startsWith,
    contains,
    endsWith,
    limit,
  ]);

  const onClickSearch = () => {
    if (context === "virtualLandline") {
      router.push(
        `/phone-number/all-numbers/?areaCode=${areaCode}&countryCode=${countryCode}&startsWith=${showAdvanced ? startsWith : ""}&contains=${showAdvanced ? contains : ""}&endsWith=${showAdvanced ? endsWith : ""}&numberCategory=${showAdvanced ? numberCategory : ""}&scroll=true&limit=${limit || 20}`
      );
      scrollToView();
    } else if (context === "individual") {
      setMounted(false);
      handleSearch();
      scrollToView();
      setShowTable?.(true);
    } else {
      setMounted(false);
      handleSearch();
      scrollToView();
    }
  };

  return (
    <S.NumberPickerTabWrapper>
      <S.NumberPickerContainer>
        <StandardOptions />
        <AnimatePresence>
          {showAdvanced && (
            <AdvancedOptions
              mounted={mounted}
              shownTable={shownTable}
              context={context}
            />
          )}
        </AnimatePresence>
        <Footer onClickSearch={onClickSearch} />
      </S.NumberPickerContainer>
    </S.NumberPickerTabWrapper>
  );
};

export default NumberPickerTab;
